/**
 * Waiting-room chat audit event schemas + path.
 *
 * Every content action (send/edit/delete/react) and every presence
 * transition produces one of these and is written append-only to
 * `/chatAudit/{eventId}`. The client builds the payload via
 * {@link buildAuditEvent}; the Firestore converter augments it with
 * a `serverTimestamp` sentinel at write time so audit rows always carry
 * server-authoritative time.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';

/** All event types emitted to the audit log. */
export const AuditEventTypeSchema = z.enum([
  'message_sent',
  'message_edited',
  'message_deleted',
  'reaction_added',
  'reaction_removed',
  'presence_join',
  'presence_leave',
  'presence_idle',
  'presence_resume',
]);

/** Inferred type for {@link AuditEventTypeSchema}. */
export type AuditEventType = z.infer<typeof AuditEventTypeSchema>;

/**
 * The client-prepared portion of an audit event — everything except the
 * `serverTimestamp` which the Firestore converter stamps on write.
 *
 * All optional context fields (messageId / snapshot / emoji / userAgent)
 * are normalized to explicit `null` so Firestore stores the same shape
 * regardless of caller.
 */
export const ClientAuditEventSchema = z
  .object({
    /** The event type. */
    eventType: AuditEventTypeSchema,
    /** The course id the event belongs to (denormalized for cheap per-course queries). */
    courseId: z.string(),
    /** The section id. */
    sectionId: z.string(),
    /** The zero-indexed section week. */
    sectionWeekId: z.number(),
    /** The actor's uid. */
    actorUid: z.string(),
    /** The actor's display name at event time. */
    actorDisplayName: z.string(),
    /** The message id the event refers to, or null for presence events. */
    messageId: z.string().nullable(),
    /** The plain-text content snapshot, or null when not applicable. */
    contentSnapshotPlainText: z.string().nullable(),
    /** The emoji involved, or null when not applicable. */
    emoji: z.string().nullable(),
    /** The user-agent string, or null when the caller did not supply one. */
    userAgent: z.string().nullable(),
    /** Client-local epoch-ms timestamp. */
    clientTimestampMs: z.number(),
  })
  .loose();

/** Inferred type for {@link ClientAuditEventSchema}. */
export type ClientAuditEvent = z.infer<typeof ClientAuditEventSchema>;

/**
 * Full stored shape: the client payload plus the server-stamped timestamp.
 * This is what readers of `/chatAudit/{eventId}` see.
 */
export const StoredAuditEventSchema = ClientAuditEventSchema.extend({
  /** Server-assigned write time (set by the client converter via `serverTimestamp()`). */
  serverTimestamp: FirestoreTimestampSchema,
});

/** Inferred type for {@link StoredAuditEventSchema}. */
export type StoredAuditEvent = z.infer<typeof StoredAuditEventSchema>;

/** Top-level Firestore collection where every chat interaction is recorded. */
export const CHAT_AUDIT_COLLECTION_PATH = 'chatAudit' as const;

/**
 * Collection path builder. Kept for symmetry with the other chat-document
 * modules and for consumers that prefer the function form.
 *
 * @returns The Firestore collection path `chatAudit`.
 */
export function getChatAuditCollectionPath(): string {
  return CHAT_AUDIT_COLLECTION_PATH;
}

/**
 * Single audit event doc path.
 *
 * @param args.eventId - The Firestore-assigned event id.
 * @returns Firestore doc path to the event.
 */
export function getChatAuditDocPath({eventId}: {eventId: string}): string {
  return `${CHAT_AUDIT_COLLECTION_PATH}/${eventId}`;
}

// ============================================================================
// Pure builder
// ============================================================================

/** Per-event contextual data supplied by callers. */
export interface AuditEventContext {
  /**
   * The course id the event belongs to. Denormalized from the parent section
   * so per-course admin queries / partitioning don't require a Firestore
   * join against `sections` per audit event.
   */
  readonly courseId: string;
  /** The section id the event occurred in. */
  readonly sectionId: string;
  /** The zero-indexed section week. */
  readonly sectionWeekId: number;
  /** The message id the event refers to (omit for presence events). */
  readonly messageId?: string;
  /** For message events: the plain-text content at event time. */
  readonly contentSnapshotPlainText?: string;
  /** For reaction events: the emoji involved. */
  readonly emoji?: string;
  /** The `navigator.userAgent` string. Optional. */
  readonly userAgent?: string;
}

/** Who caused the event. */
export interface AuditEventActor {
  /** The actor's uid. */
  readonly uid: string;
  /** The actor's display name at event time. */
  readonly displayName: string;
}

/**
 * Builds the client portion of an audit event. Optional context fields get
 * normalized to `null` so every stored event has the same shape.
 *
 * @param args.eventType - Which event this is.
 * @param args.actor - Who is causing the event.
 * @param args.context - Section/week/message context.
 * @param args.now - Reference "now" (pass explicitly — pure).
 * @returns A fully-populated {@link ClientAuditEvent}.
 */
export function buildAuditEvent({
  eventType,
  actor,
  context,
  now,
}: {
  eventType: AuditEventType;
  actor: AuditEventActor;
  context: AuditEventContext;
  now: Date;
}): ClientAuditEvent {
  return {
    eventType,
    courseId: context.courseId,
    sectionId: context.sectionId,
    sectionWeekId: context.sectionWeekId,
    actorUid: actor.uid,
    actorDisplayName: actor.displayName,
    messageId: context.messageId ?? null,
    contentSnapshotPlainText: context.contentSnapshotPlainText ?? null,
    emoji: context.emoji ?? null,
    userAgent: context.userAgent ?? null,
    clientTimestampMs: now.getTime(),
  };
}
